import { createShellStore, type ShellStoreApi, type WorkShapes } from "@hakili/app-shell";

/**
 * `P0-DLW-001`: dealer-web's shell store.
 *
 * **One persona and one screen, stated rather than left implicit.** The console
 * carries five views because a Hakili operator wears different hats; a
 * dealership's administrator wears one. Declaring the single persona now means
 * Round C's team screen is added to a list rather than introduced to a shell
 * that never had one.
 *
 * `sessionStorage` and not `localStorage`, as the console does: a shell state
 * that outlives the browser session would restore a screen for a person who has
 * since logged out. The helper is local for the same reason the console's is ,
 * a store built during a test or a server render has no `sessionStorage`.
 */
const sessionStorageOrMemory = (): Storage => {
  if (typeof globalThis.sessionStorage !== "undefined") return globalThis.sessionStorage;
  const memory = new Map<string, string>();
  return {
    get length() {
      return memory.size;
    },
    clear: () => {
      memory.clear();
    },
    getItem: (key: string) => memory.get(key) ?? null,
    key: (index: number) => [...memory.keys()][index] ?? null,
    removeItem: (key: string) => {
      memory.delete(key);
    },
    setItem: (key: string, value: string) => {
      memory.set(key, value);
    },
  };
};

export const PERSONAS = ["concessionnaire"] as const;
export type Persona = (typeof PERSONAS)[number];

/**
 * `WorkShapes` requires all three slots, so they are declared EMPTY rather than
 * omitted: dealer-web queues nothing yet, and Round C's invitations are the
 * first thing that will. An empty object per slot says "nothing is queued here"
 * where a missing slot would not compile.
 */
export interface DealerWork extends WorkShapes {
  readonly dossier: Record<string, never>;
  readonly vehicle: Record<string, never>;
  readonly partner: Record<string, never>;
}

export type DealerStore = ShellStoreApi<Persona, DealerWork>;

export function creerDealerStore(): DealerStore {
  return createShellStore<Persona, DealerWork>({
    personas: PERSONAS,
    initialPersona: "concessionnaire",
    landingScreen: () => "/",
    storage: sessionStorageOrMemory(),
    name: "hakili.dealer",
  });
}
